/**
 * The client's transport/session edge (multi-client workplan R2-light, AD-123).
 *
 * One small module owns the browser-coupled, deployment- and identity-bearing
 * concerns the rest of the client must not hardcode:
 *   - the SERVER ENDPOINT (HTTP base + gameplay WS URL), configurable for a
 *     non-same-origin deploy but defaulting to same-origin (byte-identical to the
 *     original hardcoded behaviour);
 *   - the ASSET ENDPOINT for delivered .glb/static handles. Assets default to
 *     the page origin (Vite/public or the built SPA static host), even when the
 *     API/WS server is configured on another origin. A CDN/static override is
 *     explicit.
 *   - the SESSION TOKEN (the bearer token persisted across reloads);
 *   - the gameplay SOCKET construction + readiness check;
 *   - the connection TIMERS (the RTT ping interval / the resume backoff timeout).
 *
 * Endpoint resolution order (first match wins) so one setting drives both HTTP
 * and WS:
 *   1. build-time  import.meta.env.VITE_GC_SERVER_URL  (Vite inlines it)
 *   2. runtime     window.__GC_CONFIG__.serverUrl       (a deploy may inject
 *                  <script>window.__GC_CONFIG__ = { serverUrl: '...' }</script>)
 *   3. default     same-origin: '' for the HTTP base (relative /api), and the
 *                  page origin (location) for the WS URL.
 *
 * Asset endpoint resolution is separate:
 *   1. build-time  import.meta.env.VITE_GC_ASSET_URL
 *   2. runtime     window.__GC_CONFIG__.assetUrl
 *   3. default     same-origin/page origin: '' (`/assets/...`)
 *
 * NOTE — R2 is the LIGHT slice: the gameplay socket is still a concrete browser
 * `WebSocket` (aliased `GameSocket`). This module isolates the browser GLOBALS
 * (the `new WebSocket`, `location`, `localStorage`, `window.set*` calls), not the
 * WebSocket API itself. Abstracting the transport behind an interface with
 * non-browser implementations is the FULL multi-platform adapter, deferred.
 *
 * Presentation-local UI preferences (the `pxl.*` settings/accessibility keys in
 * `ui/`) are deliberately NOT owned here — they are not transport/session/endpoint
 * state and are a separate concern from connecting to the server.
 */

const TOKEN_KEY = 'gc.token';

// --- endpoint resolution (pure cores are exported for tests) -----------------

/**
 * Pure: pick the configured server origin from the candidate overrides and
 * normalise it. Empty/unset -> '' (the same-origin default). A non-empty value
 * has any trailing slash(es) stripped so it composes cleanly with '/api/...'.
 */
export function resolveHttpBase(
  buildTime: string | undefined,
  runtime: string | undefined,
): string {
  const configured = (buildTime ?? runtime ?? '').trim();
  if (configured === '') return '';
  return configured.replace(/\/+$/, '');
}

/**
 * Pure: pick the configured static asset origin. Unlike `resolveHttpBase`, this
 * never falls back to `serverUrl`: in split dev/static deployments the API/WS
 * origin is often the backend, while `/assets/...` is served by the frontend.
 */
export function resolveAssetBase(
  buildTime: string | undefined,
  runtime: string | undefined,
): string {
  const configured = (buildTime ?? runtime ?? '').trim();
  if (configured === '') return '';
  return configured.replace(/\/+$/, '');
}

/**
 * Pure: derive the gameplay WS URL from a resolved HTTP base and the page origin.
 * Same-origin ('' base) reproduces the original `wss://<host>/ws` exactly; an
 * explicit http(s) base is converted to ws(s) so one setting drives both.
 */
export function deriveWsUrl(httpBase: string, origin: { protocol: string; host: string }): string {
  if (httpBase === '') {
    const proto = origin.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${origin.host}/ws`;
  }
  return `${httpBase.replace(/^http/i, 'ws')}/ws`;
}

function buildTimeServerUrl(): string | undefined {
  // import.meta.env is provided by Vite at build time; read it defensively so the
  // module also compiles/loads under the dependency-free test runner (no Vite, no
  // vite/client types -- import.meta.env is undefined there).
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const v = env?.VITE_GC_SERVER_URL;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function buildTimeAssetUrl(): string | undefined {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const v = env?.VITE_GC_ASSET_URL;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function runtimeServerUrl(): string | undefined {
  // window.__GC_CONFIG__ in the browser (window === globalThis); read via
  // globalThis so it is also exercisable under the Node test runner.
  const cfg = (globalThis as { __GC_CONFIG__?: { serverUrl?: string } }).__GC_CONFIG__;
  const v = cfg?.serverUrl;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function runtimeAssetUrl(): string | undefined {
  const cfg = (globalThis as { __GC_CONFIG__?: { assetUrl?: string } }).__GC_CONFIG__;
  const v = cfg?.assetUrl;
  return typeof v === 'string' && v !== '' ? v : undefined;
}

/** The HTTP base to prefix REST requests with: '' (relative /api) by default. */
export function serverHttpBase(): string {
  return resolveHttpBase(buildTimeServerUrl(), runtimeServerUrl());
}

/** The HTTP base to prefix static asset handles with: '' (`/assets/...`) by default. */
export function assetHttpBase(): string {
  return resolveAssetBase(buildTimeAssetUrl(), runtimeAssetUrl());
}

/** The gameplay WS URL: `wss://<page-host>/ws` by default, or the configured origin. */
export function serverWsUrl(): string {
  const base = serverHttpBase();
  // Only the same-origin default needs the page location; an explicit base does not.
  const origin =
    base === '' ? { protocol: location.protocol, host: location.host } : { protocol: '', host: '' };
  return deriveWsUrl(base, origin);
}

// --- session token (the persisted bearer token) ------------------------------

/** Read the persisted session token (null if unset or storage is unavailable). */
export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Persist the session token (best-effort; private-mode storage failures are ignored). */
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage unavailable (private mode) -- the session just won't survive a reload.
  }
}

/** Clear the persisted session token (best-effort). */
export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage unavailable -- nothing to do.
  }
}

// --- gameplay socket + connection timers -------------------------------------

/**
 * The gameplay socket type. R2-light keeps the concrete browser `WebSocket`; this
 * alias marks the seam where the deferred full transport adapter would slot in.
 */
export type GameSocket = WebSocket;

/** Open the gameplay socket (the single `new WebSocket` site). */
export function createGameSocket(url: string): GameSocket {
  const ws = new WebSocket(url);
  // D4 `q-snapshot-binary-wire`: binary snapshot frames arrive as ArrayBuffer (not Blob), so they
  // decode synchronously in order with the JSON messages around them.
  ws.binaryType = 'arraybuffer';
  return ws;
}

/** Whether a socket is open and ready to send. */
export function socketOpen(socket: GameSocket): boolean {
  return socket.readyState === WebSocket.OPEN;
}

/** A browser timer handle (adapts to whichever lib is in scope: number | NodeJS.Timeout). */
export type TimerHandle = ReturnType<typeof setInterval>;

/** Start a repeating timer (the RTT ping). */
export function intervalTimer(handler: () => void, ms: number): TimerHandle {
  return setInterval(handler, ms);
}

/** Start a one-shot timer (the resume backoff). */
export function timeoutTimer(handler: () => void, ms: number): TimerHandle {
  return setTimeout(handler, ms);
}

/** Cancel a timer started above. `clearTimeout` cancels both kinds (shared timer-id space, HTML spec). */
export function clearTimer(handle: TimerHandle | null): void {
  if (handle !== null) clearTimeout(handle);
}
